import type { CodeMigration } from './types.js';

/**
 * Retroactive migration for v5.26.0 — migrations BEFORE `InitializeSmartStackAsync`.
 *
 * `InitializeSmartStackAsync` runs the client seed providers (setup values, the
 * test dataset — scaffold-seed), and those write CLIENT tables. The historical
 * Program.cs applied the client migrations only AFTER it (`SmartStackDbInitializer
 * .ApplyAllAsync(coreDb, extDb)`), so on a FRESH database (new machine, CI, the
 * release rehearsal) the first boot crashed: the platform rethrows a failing
 * provider and the client tables did not exist yet. A database migrated by
 * `/ba-develop` (efcore apply) hid it.
 *
 * The fix moves the `ApplyAllAsync` scope block (Core → Extensions, idempotent,
 * no dependency on the initialisation) just above `await app.InitializeSmartStackAsync();`
 * and its comment lines. `InitializeSmartStackAsync` then re-checks the Core
 * migrations (a no-op) and seeds a complete schema. Idempotent: once the block
 * precedes the call, `detect` is false. A Program.cs whose shape is not
 * recognised is left alone (detect false) — never a half move.
 */
export const programCsMigrationsBeforeInitMigration: CodeMigration = {
  axis: 'cli',
  fromVersion: '1.0.0',
  toVersion: '5.26.0',
  description: 'Program.cs: apply the Core + client migrations BEFORE InitializeSmartStackAsync (fresh databases boot)',

  migrations: [
    {
      pattern: '**/Program.cs',
      description: 'Move the SmartStackDbInitializer.ApplyAllAsync block before InitializeSmartStackAsync',
      detect: (content: string): boolean => planMove(content) !== null,
      transform: (content: string): string => {
        const plan = planMove(content);
        if (!plan) return content;
        const { blockStart, blockEnd, insertAt } = plan;
        const eol = content.includes('\r\n') ? '\r\n' : '\n';
        const block = content.slice(blockStart, blockEnd).replace(/\s+$/, '');
        const indent = /^[ \t]*/.exec(content.slice(insertAt))?.[0] ?? '';
        const note =
          `${indent}// Migrations FIRST (Core → Extensions): InitializeSmartStackAsync runs the client seed${eol}` +
          `${indent}// providers (setup values, test dataset), which write client tables — on a fresh database${eol}` +
          `${indent}// they must exist before it. Idempotent — safe on every startup.${eol}`;
        const withoutBlock = content.slice(0, blockStart) + content.slice(blockEnd).replace(/^\r?\n/, '');
        // insertAt precedes blockStart: its offset is unchanged by the removal.
        return withoutBlock.slice(0, insertAt) + note + block + eol + eol + withoutBlock.slice(insertAt);
      },
    },
  ],
};

interface MovePlan {
  /** Start of the `using (… CreateScope())` block, its leading comment lines included. */
  blockStart: number;
  /** End of the block (after its closing brace and the line break). */
  blockEnd: number;
  /** Where the block goes: above `await app.InitializeSmartStackAsync();` and its leading comment lines. */
  insertAt: number;
}

/** Where the ApplyAllAsync block is and where it goes — null when there is nothing (or nothing safe) to move. */
function planMove(content: string): MovePlan | null {
  const init = /await\s+app\.InitializeSmartStackAsync\s*\(\s*\)\s*;/.exec(content);
  const apply = /\.ApplyAllAsync\s*\(/.exec(content);
  if (!init || !apply || apply.index < init.index) return null;
  // The scope block holding the call: the last `using (… CreateScope())` before it.
  const usingRe = /using\s*\(\s*var\s+\w+\s*=\s*app\.Services\.CreateScope\(\)\s*\)/g;
  let usingAt = -1;
  let m: RegExpExecArray | null;
  while ((m = usingRe.exec(content)) !== null) {
    if (m.index > apply.index) break;
    if (m.index > init.index) usingAt = m.index;
  }
  if (usingAt === -1) return null;
  const open = content.indexOf('{', usingAt);
  if (open === -1 || open > apply.index) return null;
  let depth = 0;
  let close = -1;
  for (let i = open; i < content.length; i++) {
    if (content[i] === '{') depth++;
    else if (content[i] === '}') {
      depth--;
      if (depth === 0) {
        close = i;
        break;
      }
    }
  }
  if (close === -1 || close < apply.index) return null;
  const blockStart = withLeadingComments(content, content.lastIndexOf('\n', usingAt - 1) + 1);
  if (blockStart <= init.index) return null;
  const lineEnd = content.indexOf('\n', close);
  const blockEnd = lineEnd === -1 ? content.length : lineEnd + 1;
  const insertAt = withLeadingComments(content, content.lastIndexOf('\n', init.index - 1) + 1);
  return { blockStart, blockEnd, insertAt };
}

/** The start of the contiguous `//` comment lines directly above the line starting at `lineStart`. */
function withLeadingComments(content: string, lineStart: number): number {
  let start = lineStart;
  for (;;) {
    const prevEnd = start - 1;
    if (prevEnd <= 0) break;
    const prevStart = content.lastIndexOf('\n', prevEnd - 1) + 1;
    if (/^[ \t]*\/\//.test(content.slice(prevStart, prevEnd))) start = prevStart;
    else break;
  }
  return start;
}
